// src/utils/hardwareScanner.js
//
// Глобальный «wedge»-слушатель для беспроводного HID-сканера
// (Bluetooth/USB в режиме клавиатуры). Сканер печатает код символ-за-символом
// и в конце жмёт Enter. Скан отличаем от ручного ввода по скорости: символы
// летят быстро друг за другом — человек так печатать не может.
//
// Готовый код отдаём ВЕРХНЕМУ зарегистрированному обработчику — тому же
// onScan, что дёргает камера. Нативный код/плагин не нужен.
//
// Порог скорости (GAP_MS) вынесен в настройку: у Bluetooth-сканеров интервал
// между символами больше, чем у USB (60–100 мс против <10 мс), плюс джиттер.
// Слишком узкий порог = сканер не распознаётся, слишком широкий = может
// перехватить быстрый ручной набор. Значение по умолчанию — компромисс,
// при необходимости меняется без пересборки:
//   localStorage.setItem('hwScannerGapMs', '130')

const DEFAULT_GAP_MS = 100;   // СРЕДНИЙ интервал между символами для «скана»
const CONTINUE_GAP   = 250;   // одиночный разрыв больше — считаем новым вводом
const MIN_LEN        = 3;     // короче — не считаем сканом

function readGap() {
  try {
    const v = parseInt(localStorage.getItem('hwScannerGapMs') || '', 10);
    if (v >= 20 && v <= 400) return v;
  } catch (e) { /* noop */ }
  return DEFAULT_GAP_MS;
}
let GAP_MS = readGap();

let enabled = true;
try {
  if (localStorage.getItem('hwScannerEnabled') === '0') enabled = false;
} catch (e) { /* localStorage может быть недоступен */ }

const handlers = [];  // стек; верхний (последний смонтированный) получает скан

let buffer = '';
let firstT = 0;       // время первого символа бурста
let lastT = 0;        // время предыдущего символа
let armed = false;    // похоже на скан (быстрый бурст, начатый «кодовым» символом)

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

function reset() { buffer = ''; armed = false; }

// Сканер «печатает» клавишами, и при русской раскладке ОС латиница QR
// клиента ({"type":"customer",...}) приходит кириллицей: { → Х, " → Э, t → е.
// Цифровые штрихкоды раскладка не портит. Если код начинается с «Х»
// (Shift+[ в русской) — возвращаем символы на латинские клавиши.
const RU = 'ёйцукенгшщзхъфывапролджэячсмитьбю.ЁЙЦУКЕНГШЩЗХЪФЫВАПРОЛДЖЭЯЧСМИТЬБЮ,"№;:?';
const EN = '`qwertyuiop[]asdfghjkl;\'zxcvbnm,./~QWERTYUIOP{}ASDFGHJKL:"ZXCVBNM<>?@#$^&';
function fixLayout(code) {
  if (code[0] !== 'Х') return code;
  let out = '';
  for (const ch of code) {
    const i = RU.indexOf(ch);
    out += i >= 0 ? EN[i] : ch;
  }
  return out;
}

function dispatch(code) {
  code = fixLayout(code);
  const top = handlers[handlers.length - 1];
  if (top) {
    try { top(code); } catch (e) { console.error('[hwScanner] handler error', e); }
  }
}

// Код сканера начинается с цифры (EAN/UPC) или с '01'+GTIN (DataMatrix),
// либо с символа-разделителя GS, либо с «{» — QR клиента (JSON); «Х» — это
// та же «{» при русской раскладке. Ручной поиск товара обычно начинается
// с буквы — так отсекаем перехват обычного набора.
function codeLike(ch) { return /[0-9\x1d{Х]/.test(ch); }


function onKeydown(e) {
  if (!enabled || handlers.length === 0) return;
  if (e.ctrlKey || e.metaKey || e.altKey) { reset(); return; }

  if (e.key === 'Enter') {
    if (armed && buffer.length >= MIN_LEN) {
      e.preventDefault();
      const code = buffer;
      reset();
      dispatch(code);
    } else {
      reset();
    }
    return;
  }

  if (e.key.length === 1) {
    const t = now();
    const gap = t - lastT;

    if (buffer.length > 0 && gap <= CONTINUE_GAP) {
      // продолжаем бурст даже при одиночной задержке Bluetooth
      buffer += e.key;
    } else {
      // разрыв слишком большой — новый ввод с начала
      buffer = e.key;
      firstT = t;
      armed = false;
    }

    // «Скан» определяем по СРЕДНЕМУ интервалу за весь бурст: одиночный
    // скачок задержки его не сорвёт, а человек по среднему всё равно
    // печатает медленно. Код должен начинаться с цифры/разделителя —
    // так не перехватываем обычный текстовый поиск (он с буквы).
    if (buffer.length >= 2 && codeLike(buffer[0])) {
      const avg = (t - firstT) / (buffer.length - 1);
      armed = avg <= GAP_MS;
    }

    if (armed) e.preventDefault();  // не даём коду сыпаться в активное поле
    lastT = t;
  }
}

let installed = false;
function ensureInstalled() {
  if (installed || typeof document === 'undefined') return;
  document.addEventListener('keydown', onKeydown, true); // capture-фаза
  installed = true;
}

// Зарегистрировать обработчик железного скана. Возвращает функцию отписки.
export function pushScanHandler(fn) {
  ensureInstalled();
  handlers.push(fn);
  return () => {
    const i = handlers.lastIndexOf(fn);
    if (i >= 0) handlers.splice(i, 1);
  };
}

export function setHardwareScannerEnabled(on) {
  enabled = !!on;
  try { localStorage.setItem('hwScannerEnabled', on ? '1' : '0'); } catch (e) { /* noop */ }
}

export function isHardwareScannerEnabled() { return enabled; }

// Подстройка порога скорости под конкретный сканер (мс). Полезно, если
// сканер медленный/быстрый и распознаётся нестабильно.
export function setHardwareScannerGap(ms) {
  const v = parseInt(ms, 10);
  if (v >= 20 && v <= 400) {
    GAP_MS = v;
    try { localStorage.setItem('hwScannerGapMs', String(v)); } catch (e) { /* noop */ }
  }
}

export function getHardwareScannerGap() { return GAP_MS; }
